import React, { useEffect, useState } from 'react';
import { supplementsAPI, membersAPI } from '../../utils/api';
import { formatDate, formatCurrency } from '../../utils/helpers';
import Modal from '../../components/Modal';
import ConfirmModal from '../../components/ConfirmModal';
import LoadingSpinner from '../../components/LoadingSpinner';
import toast from 'react-hot-toast';

const defaultSupForm = { name: '', brand: '', stock: '', purchasePrice: '', sellingPrice: '', lowStockThreshold: 5 };
const defaultSellForm = { supplementId: '', buyerType: 'Member', memberId: '', walkinName: '', walkinPhone: '', quantity: '1' };

function Supplements() {
  const [supplements, setSupplements] = useState([]);
  const [sales, setSales] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('inventory');
  const [supModal, setSupModal] = useState(false);
  const [sellModal, setSellModal] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, id: null });
  const [supForm, setSupForm] = useState(defaultSupForm);
  const [sellForm, setSellForm] = useState(defaultSellForm);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [supRes, salesRes, memRes] = await Promise.all([
        supplementsAPI.getAll(),
        supplementsAPI.getSales(),
        membersAPI.getAll()
      ]);
      setSupplements(supRes.data);
      setSales(salesRes.data);
      setMembers(memRes.data);
    } catch {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleSupSubmit = async e => {
    e.preventDefault();
    try {
      if (editTarget) {
        await supplementsAPI.update(editTarget, supForm);
        toast.success('Supplement updated!');
      } else {
        await supplementsAPI.create(supForm);
        toast.success('Supplement added!');
      }
      setSupModal(false);
      setSupForm(defaultSupForm);
      setEditTarget(null);
      fetchData();
    } catch {
      toast.error('Failed to save supplement');
    }
  };

  const handleEdit = (sup) => {
    setEditTarget(sup._id);
    setSupForm({
      name: sup.name, brand: sup.brand, stock: sup.stock,
      purchasePrice: sup.purchasePrice, sellingPrice: sup.sellingPrice,
      lowStockThreshold: sup.lowStockThreshold
    });
    setSupModal(true);
  };

  const handleDelete = async () => {
    try {
      await supplementsAPI.delete(deleteModal.id);
      toast.success('Supplement deleted');
      setDeleteModal({ open: false, id: null });
      fetchData();
    } catch {
      toast.error('Delete failed');
    }
  };

  const handleSell = async e => {
    e.preventDefault();
    try {
      const sup = supplements.find(s => s._id === sellForm.supplementId);
      if (!sup) return;
      const total = sup.sellingPrice * parseInt(sellForm.quantity);
      await supplementsAPI.sell(sellForm);
      toast.success(`Sale recorded! Total: ${formatCurrency(total)}`);
      setSellModal(false);
      setSellForm(defaultSellForm);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Sale failed');
    }
  };

  const lowStockItems = supplements.filter(s => s.stock <= s.lowStockThreshold);
  const totalRevenue = sales.reduce((s, sale) => s + sale.totalAmount, 0);

  if (loading) return <LoadingSpinner />;

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Supplements</h2>
          {lowStockItems.length > 0 && (
            <p className="text-red-500 text-sm font-medium">⚠️ {lowStockItems.length} item(s) low on stock</p>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={() => { setEditTarget(null); setSupForm(defaultSupForm); setSupModal(true); }} className="btn-secondary text-sm">
            + Add Supplement
          </button>
          <button onClick={() => setSellModal(true)} className="btn-primary text-sm">
            💊 Sell
          </button>
        </div>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card text-center">
          <p className="text-2xl font-bold text-blue-600">{supplements.length}</p>
          <p className="text-sm text-gray-500 mt-1">Total Products</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-red-600">{lowStockItems.length}</p>
          <p className="text-sm text-gray-500 mt-1">Low Stock</p>
        </div>
        <div className="card text-center">
          <p className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</p>
          <p className="text-sm text-gray-500 mt-1">Total Revenue</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {['inventory', 'sales'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium capitalize transition-colors ${activeTab === tab ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab === 'inventory' ? 'Inventory' : 'Sales History'}
          </button>
        ))}
      </div>

      {activeTab === 'inventory' && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Name / Brand</th>
                  <th className="table-header">Stock</th>
                  <th className="table-header">Buy Price</th>
                  <th className="table-header">Sell Price</th>
                  <th className="table-header">Margin</th>
                  <th className="table-header">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {supplements.map(sup => (
                  <tr key={sup._id} className={`hover:bg-gray-50 ${sup.stock <= sup.lowStockThreshold ? 'bg-red-50' : ''}`}>
                    <td className="table-cell">
                      <p className="font-medium text-sm text-gray-800">{sup.name}</p>
                      <p className="text-xs text-gray-400">{sup.brand}</p>
                    </td>
                    <td className="table-cell">
                      <span className={`font-bold text-sm ${sup.stock <= sup.lowStockThreshold ? 'text-red-600' : 'text-gray-700'}`}>
                        {sup.stock} units
                      </span>
                      {sup.stock <= sup.lowStockThreshold && (
                        <span className="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded">Low</span>
                      )}
                    </td>
                    <td className="table-cell">{formatCurrency(sup.purchasePrice)}</td>
                    <td className="table-cell font-medium">{formatCurrency(sup.sellingPrice)}</td>
                    <td className="table-cell text-green-600">{formatCurrency(sup.sellingPrice - sup.purchasePrice)}</td>
                    <td className="table-cell">
                      <div className="flex gap-1">
                        <button onClick={() => handleEdit(sup)} className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg">✏️</button>
                        <button onClick={() => setDeleteModal({ open: true, id: sup._id })} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {supplements.length === 0 && (
              <div className="text-center py-12 text-gray-400">No supplements in inventory</div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'sales' && (
        <div className="card p-0 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="table-header">Product</th>
                  <th className="table-header">Buyer</th>
                  <th className="table-header">Qty</th>
                  <th className="table-header">Amount</th>
                  <th className="table-header">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {sales.map(s => (
                  <tr key={s._id} className="hover:bg-gray-50">
                    <td className="table-cell">
                      <p className="font-medium text-sm">{s.supplementId?.name}</p>
                      <p className="text-xs text-gray-400">{s.supplementId?.brand}</p>
                    </td>
                    <td className="table-cell">
                      <p className="text-sm">{s.memberId?.name || s.walkinName || 'Walk-in'}</p>
                      <p className="text-xs text-gray-400">{s.buyerType}</p>
                    </td>
                    <td className="table-cell font-medium">{s.quantity}</td>
                    <td className="table-cell font-medium text-green-600">{formatCurrency(s.totalAmount)}</td>
                    <td className="table-cell text-xs">{formatDate(s.saleDate)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {sales.length === 0 && (
              <div className="text-center py-12 text-gray-400">No sales recorded</div>
            )}
          </div>
        </div>
      )}

      {/* Add/Edit Supplement Modal */}
      <Modal isOpen={supModal} onClose={() => { setSupModal(false); setEditTarget(null); setSupForm(defaultSupForm); }} title={editTarget ? 'Edit Supplement' : 'Add Supplement'}>
        <form onSubmit={handleSupSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Name *</label>
              <input className="input" value={supForm.name} onChange={e => setSupForm(f => ({ ...f, name: e.target.value }))} placeholder="Product name" required />
            </div>
            <div>
              <label className="label">Brand *</label>
              <input className="input" value={supForm.brand} onChange={e => setSupForm(f => ({ ...f, brand: e.target.value }))} placeholder="Brand name" required />
            </div>
            <div>
              <label className="label">Stock (units) *</label>
              <input className="input" type="number" value={supForm.stock} onChange={e => setSupForm(f => ({ ...f, stock: e.target.value }))} min="0" required />
            </div>
            <div>
              <label className="label">Low Stock Alert (units)</label>
              <input className="input" type="number" value={supForm.lowStockThreshold} onChange={e => setSupForm(f => ({ ...f, lowStockThreshold: e.target.value }))} min="1" />
            </div>
            <div>
              <label className="label">Purchase Price (₹) *</label>
              <input className="input" type="number" value={supForm.purchasePrice} onChange={e => setSupForm(f => ({ ...f, purchasePrice: e.target.value }))} required />
            </div>
            <div>
              <label className="label">Selling Price (₹) *</label>
              <input className="input" type="number" value={supForm.sellingPrice} onChange={e => setSupForm(f => ({ ...f, sellingPrice: e.target.value }))} required />
            </div>
          </div>
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => { setSupModal(false); setEditTarget(null); }} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">{editTarget ? 'Update' : 'Add Supplement'}</button>
          </div>
        </form>
      </Modal>

      {/* Sell Modal */}
      <Modal isOpen={sellModal} onClose={() => setSellModal(false)} title="Sell Supplement">
        <form onSubmit={handleSell} className="space-y-4">
          <div>
            <label className="label">Supplement *</label>
            <select className="input" value={sellForm.supplementId} onChange={e => setSellForm(f => ({ ...f, supplementId: e.target.value }))} required>
              <option value="">Select supplement</option>
              {supplements.filter(s => s.stock > 0).map(s => (
                <option key={s._id} value={s._id}>{s.name} ({s.brand}) - {s.stock} in stock - {formatCurrency(s.sellingPrice)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Buyer Type</label>
            <select className="input" value={sellForm.buyerType} onChange={e => setSellForm(f => ({ ...f, buyerType: e.target.value }))}>
              <option>Member</option>
              <option>Walk-in</option>
            </select>
          </div>
          {sellForm.buyerType === 'Member' ? (
            <div>
              <label className="label">Select Member</label>
              <select className="input" value={sellForm.memberId} onChange={e => setSellForm(f => ({ ...f, memberId: e.target.value }))}>
                <option value="">Select member</option>
                {members.map(m => <option key={m._id} value={m._id}>{m.name} ({m.memberId})</option>)}
              </select>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Customer Name</label>
                <input className="input" value={sellForm.walkinName} onChange={e => setSellForm(f => ({ ...f, walkinName: e.target.value }))} placeholder="Name" />
              </div>
              <div>
                <label className="label">Phone</label>
                <input className="input" value={sellForm.walkinPhone} onChange={e => setSellForm(f => ({ ...f, walkinPhone: e.target.value }))} placeholder="Phone" />
              </div>
            </div>
          )}
          <div>
            <label className="label">Quantity *</label>
            <input className="input" type="number" min="1" value={sellForm.quantity} onChange={e => setSellForm(f => ({ ...f, quantity: e.target.value }))} required />
          </div>
          {sellForm.supplementId && sellForm.quantity && (
            <div className="p-3 bg-green-50 rounded-lg text-sm text-green-700 font-medium">
              Total: {formatCurrency((supplements.find(s => s._id === sellForm.supplementId)?.sellingPrice || 0) * parseInt(sellForm.quantity || 0))}
            </div>
          )}
          <div className="flex gap-3 justify-end pt-2">
            <button type="button" onClick={() => setSellModal(false)} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary">Record Sale</button>
          </div>
        </form>
      </Modal>

      <ConfirmModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, id: null })}
        onConfirm={handleDelete}
        title="Delete Supplement"
        message="Are you sure you want to delete this supplement?"
      />
    </div>
  );
}

export default Supplements;
